import classNames from 'classnames';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import LanguageButtonSwitcher from '../LanguageButtonSwitcher/LanguageButtonSwitcher';
import MultiLanguageInputErrorText from './components/MultiLanguageInputErrorText/MultiLanguageInputErrorText';
import MultiLanguageInputPlaceholder from './components/MultiLanguageInputPlaceholder/MultiLanguageInputPlaceholder';
import { IMultiLanguageInputProps } from './types';
import { localeStore } from 'src/mobx/localesStore';
import { LOCALES } from 'src/constants/main';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import { isMultiLanguageValid } from '~/helpers';
import './style.scss';

const MultiLanguageInput: React.FunctionComponent<IMultiLanguageInputProps> = ({
  title,
  enText,
  arText,
  onChangeValue,
  // eslint-disable-next-line
  required,
  defaultEnText,
  defaultArText,
  placeholder,
  validation,
  tooltip,
  className = '',
  inputClassName = '',
  wrapperClassName = '',
  placeholderClassName = '',
  //
  onChange,
  onInput,
  // eslint-disable-next-line
  value,
  // eslint-disable-next-line
  defaultValue,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [currentLanguage, setCurrentLanguage] = useState<LOCALES>(localeStore.language === LOCALES.AR ? LOCALES.AR : LOCALES.EN);
  const [enValue, setEnValue] = useState<string | undefined>(enText || defaultEnText);
  const [arValue, setArValue] = useState<string | undefined>(arText || defaultArText);

  const isValid: boolean = isMultiLanguageValid(validation);
  const isCurrentEnLanguage = useMemo((): boolean => currentLanguage === LOCALES.EN, [currentLanguage]);
  const inputValue = useMemo((): string => (isCurrentEnLanguage ? enValue || '' : arValue || ''), [isCurrentEnLanguage, enValue, arValue]);
  const inputDir = useMemo((): 'rtl' | 'ltl' => (isCurrentEnLanguage ? 'ltl' : 'rtl'), [isCurrentEnLanguage]);

  useEffect(() => {
    if (enText !== null && enText !== enValue) setEnValue(enText);
    if (arText !== null && arText !== arValue) setArValue(arText);

    // WARNING !!! not add enValue or arValue in dependencies
  }, [enText, arText]);

  const setFocus = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [inputRef]);

  const changeCurrentLanguage = useCallback((buttonLocale?: LOCALES) => {
    if (buttonLocale) {
      setCurrentLanguage(buttonLocale);
    }
    setFocus();
  }, []);

  const _onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (isCurrentEnLanguage) {
        setEnValue(value);
      } else {
        setArValue(value);
      }

      onChangeValue(value, currentLanguage);
    },
    [onChange, onInput, onChangeValue, currentLanguage, isCurrentEnLanguage]
  );

  const _inputWrapperClassName = useMemo((): string => {
    return classNames('multi-lng-input__wrapper', className, {
      'input-error': !isValid,
      'multi-lng-input__wrapper_disabled': props.disabled,
    });
  }, [className, isValid, props.disabled]);

  const renderInput = (): JSX.Element => {
    if (title) {
      return (
        <InputAlt
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          ref={inputRef}
          value={inputValue}
          placeholder={localeStore.t(title)}
          wrapperClassName="full-width"
          className={classNames('multi-lng-input__input titled', inputClassName)}
          dir={inputDir}
          required
          onChange={_onChange}
        />
      );
    }

    return (
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        ref={inputRef}
        type="text"
        value={inputValue}
        dir={inputDir}
        onChange={_onChange}
        className={classNames('multi-lng-input__input', [inputClassName])}
      />
    );
  };

  return (
    <div className={classNames('d-flex flex-column', [wrapperClassName])}>
      <div className={_inputWrapperClassName}>
        <MultiLanguageInputPlaceholder
          placeholder={placeholder}
          currentLanguage={currentLanguage}
          enValue={enValue}
          arValue={arValue}
          placeholderClassName={placeholderClassName}
        />
        {tooltip ? (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <CustomTooltip {...tooltip} wrapperClassName={classNames('full-width', [tooltip.wrapperClassName])}>
            {renderInput()}
          </CustomTooltip>
        ) : (
          renderInput()
        )}
        <LanguageButtonSwitcher language={currentLanguage} setLanguage={changeCurrentLanguage} validation={validation} className="mx-1" />
      </div>

      <MultiLanguageInputErrorText isValid={isValid} currentLanguage={currentLanguage} validation={validation} />
    </div>
  );
};

export default MultiLanguageInput;
